import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AuthService } from '@app/core/auth';
import { environment } from '@app/env-service';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root',
})
export class KitService {
  url :string = environment.url;

  curPipe :CurrencyPipe;
  datePipe :DatePipe;

  private readonly _kits = new BehaviorSubject<any>(<any>{});
  public kits$ = this._kits.asObservable();

  public gettingRecords :boolean = false;

  private readonly _activeRecord = new BehaviorSubject<any>(<any>{});
  public activeRecord$ = this._activeRecord.asObservable();

  private readonly _record_channel_shipping = new BehaviorSubject<any>(<any>{});
  public record_channel_shipping$ = this._record_channel_shipping.asObservable();

  materialCosts :any = {
    computed_weighted: 'Weighted',
    computed_fifo: 'FIFO',
    computed_lastpo: 'Latest PO',
  };

  weightUnits :any[] = ['lbs', 'oz'];
  constructor(
    private translate :TranslateService,
    private http :HttpClient,
  ) {
    this.curPipe = new CurrencyPipe('en');
    this.datePipe = new DatePipe('en');
  }

  getAsync(params :any) :Observable<any> {
    return this.http.get(`${this.url}/kits`, { params: params });
  }

  get(params) {
    this.gettingRecords = true;

    return this.http
      .get(`${this.url}/kits`, { params: params })
      .subscribe((resp :any) => {
        this.gettingRecords = false;
        this._kits.next(resp);
      });
  }

  getById(kitId :number) :Observable<any> {
    return this.http.get(`${this.url}/kits/${kitId}`);
  }

  setActive(kit :any) {
    this._activeRecord.next(kit);
  }

  getActive() {
    return this._activeRecord.getValue();
  }

  updateKit(kitId :number, orderData :any) :Observable<any> {
    return this.http.post(`${this.url}/kits/${kitId}`, orderData).pipe(
      tap((resp :any) => {
        this.tryUpdateOrAddResp(resp);
      }),
    );
  }

  createKit(payload :any) :Observable<any> {
    return this.http.post(`${this.url}/kits`, payload).pipe(
      tap((resp :any) => {
        setTimeout(() => {
          const kits :any = this._kits.getValue();
          if (kits && kits.data) {
            kits.data.splice(0, 0, resp);
            this._kits.next(kits);
          }
        });
      }),
    );
  }

  deleteKit(kitId :number) {
    return this.http.delete(`${this.url}/kits/${kitId}`).pipe(
      tap((resp :any) => {
        const kits :any = this._kits.getValue();
        const poIdx = kits.data.findIndex((row) => row.id == kitId);
        kits.data.splice(poIdx, 1);
        this._kits.next(kits);
      }),
    );
  }

  getBasePrices(kitId) :Observable<any> {
    return this.http.get(`${this.url}/kits/${kitId}/base_prices`);
  }

  getActiveRetailPricesById(kitId :number) :Observable<any> {
    return this.http.get(`${this.url}/kits/${kitId}/active_retail_prices`, {});
  }

  getChannelShipping(params) {
    this.gettingRecords = true;

    return this.http
      .get(`${this.url}/kits/channel_shipping`, { params: params })
      .subscribe((resp :any) => {
        this._record_channel_shipping.next(resp);
        this.gettingRecords = false;
      });
  }

  updateChannelShipping(payload) :Observable<any> {
    return this.http.post(`${this.url}/kits/channel_shipping`, payload);
  }

  getSkuUnitCosts(sku :any) {
    let unitCosts :any = {
      weighted: 0,
      fifo: 0,
      lastpo: 0,
      computed_weighted: 0,
      computed_fifo: 0,
      computed_lastpo: 0,
      total_cost_defective_products: 0,
      total_item_shipped: 0,
    };
    if (sku.base_price) {
      unitCosts = Object.assign({}, sku.base_price);
    }

    return unitCosts;
  }

  tryUpdateOrAddResp(resp) {
    const kits :any = this._kits.getValue();
    if (!kits || !kits.data) {
      return;
    }
    const poIdx = kits.data.findIndex((row) => row.id == resp.id);
    if (poIdx >= 0) {
      kits.data[poIdx] = resp;
    } else {
      kits.data.splice(0, 0, resp);
    }
    this._kits.next(kits);
  }

  // update this
  uploadAttachments(id :number, fileData :FormData) :Observable<any> {
    return this.http.post(`${this.url}/kits/${id}/attachments`, fileData).pipe(
      tap((resp :any) => {
        const record = this._kits.getValue();
        if (record && record.data && record.data.length > 0) {
          const idx = record.data.findIndex((row) => {
            return row.id == id;
          });
          const kit = record.data[idx];
          if (kit && kit.attachments) {
            kit.attachments.push(resp);
          } else {
            kit.attachments = [resp];
          }
          record.data.splice(idx, 1, kit);
          this._kits.next(record);
        }
        // let rfq = data.record;
        // rfq.attachments = data.attachments;
        // this.updateRowData(rfq);
      }),
    );
  }

  deleteAttachment(kitId :number, fileId :number) :Observable<any> {
    return this.http.delete(`${this.url}/kits/${kitId}/attachments/${fileId}`);
  }

  saveDiscountStructure(kitId :number, discountStructure :any) :Observable<any> {
    return this.http.post(
      `${this.url}/kits/${kitId}/discount_structure`,
      discountStructure,
    );
  }

  saveMarkupStructure(kitId :number, markupStructure :any) :Observable<any> {
    return this.http.post(
      `${this.url}/kits/${kitId}/markup_structure`,
      markupStructure,
    );
  }

  setMaterialCost(kitId, payload) :Observable<any> {
    return this.http.post(`${this.url}/kits/${kitId}/material_cost`, payload);
  }

  searchSKU(params :any) :Observable<any> {
    return this.http.get(`${this.url}/kits/search_sku`, { params: params });
  }

  searchKitsDropdown(params :any) :Observable<any> {
    return this.http.get(`${this.url}/kits/search_kits_dropdown`, {
      params: params,
    });
  }

  search(params :any) :Observable<any> {
    return this.http.get(`${this.url}/kits/search`, { params: params });
  }

  setKitSalesMembers(kit_id :number, userList :any) :Observable<any> {
    return this.http
      .post(`${this.url}/kits/${kit_id}/kit_sales_members`, userList)
      .pipe(
        tap((resp) => {
          const record = this._kits.getValue();
          if (resp.kit && record.data) {
            const idx = record.data.findIndex((row) => {
              return row.id == kit_id;
            });
            const kit = resp.kit;
            record.data.splice(idx, 1, kit);
            this._kits.next(record);
          }
        }),
      );
  }

  setKitMembers(kit_id :number, userList :any) :Observable<any> {
    return this.http.post(`${this.url}/kits/${kit_id}/kit_members`, userList).pipe(
      tap((resp) => {
        const record = this._kits.getValue();
        if (resp.kit && record.data) {
          const idx = record.data.findIndex((row) => {
            return row.id == kit_id;
          });
          const kit = resp.kit;
          record.data.splice(idx, 1, kit);
          this._kits.next(record);
        }
      }),
    );
  }

  setKitAdmins(kit_id :number, userList :any) :Observable<any> {
    return this.http.post(`${this.url}/kits/${kit_id}/kit_admins`, userList).pipe(
      tap((resp) => {
        const record = this._kits.getValue();
        if (resp.kit && record.data) {
          const idx = record.data.findIndex((row) => {
            return row.id == kit_id;
          });
          const kit = resp.kit;
          record.data.splice(idx, 1, kit);
          this._kits.next(record);
        }
      }),
    );
  }

  viewImage(id :number) {
    return this.http.get(`${this.url}/kit-image/${id}`);
  }

  uploadImages(payload :FormData, page ?:string) {
    return this.http.post(`${this.url}/kit-image`, payload).pipe(
      tap((resp :any) => {
        if (resp) {
          const activeKit :any = this._activeRecord.getValue();
          if (activeKit && activeKit.id == payload.get('kit_id')) {
            resp.forEach((image) => {
              activeKit.images.push(image);
            });
            this._activeRecord.next(activeKit);
          }
        }
      }),
    );
  }

  deleteImage(kitId :number, imageId :number) {
    return this.http.delete(`${this.url}/kit-image/${imageId}`).pipe(
      tap((resp :any) => {
        if (resp) {
          const activeKit :any = this._activeRecord.getValue();
          if (activeKit && activeKit.id == kitId) {
            const idx = activeKit.images.findIndex((row) => {
              return row.id == imageId;
            });
            activeKit.images.splice(idx, 1);
            this._activeRecord.next(activeKit);
          }
        }
      }),
    );
  }

  bulkUpdate(payload :any) :Observable<any> {
    return this.http.post(`${this.url}/kits/bulk/update`, payload).pipe(
      tap((resp :any) => {
        const record = this._kits.getValue();
        if (record && record.data && record.data.length > 0) {
          resp.kits.forEach((kit) => {
            this.tryUpdateOrAddResp(kit);
          });
        }
      }),
    );
  }

  setKits(kits :any) :void {
    this._kits.next(kits);
  }
}
